/**
 * Espace personnel partenaire — Bénéficiaire Affiliation.
 *
 * Miroir strict de `EspacePartenaire.jsx` (Royalties) adapté aux
 * commissions d'affiliation :
 *   - Pas de taux global (les commissions dépendent de chaque code).
 *   - KPI : Ventes TTC · Commissions HT · Progression vs période N-1.
 *   - Ajout d'une section « Mes sites associés » (rôle apporteur d'affaires).
 *   - Historique transactions filtrable par période (Semaine · Mois · Année ·
 *     Personnalisé) — miroir des « Relevés Royalties » — avec sous-section
 *     dédiée « Remboursements / annulations », icône œil pour le détail,
 *     et export PDF.
 *
 * Un partenaire (rôle `partenaire`) accède UNIQUEMENT à cette page :
 * pas de sidebar, pas de menu, pas d'autre route accessible. Toutes
 * les données présentées ici sont scopées côté serveur via
 * `user.partner_commission_beneficiary_id` (endpoints /api/affiliate/me/*).
 * Les cumuls proviennent de `db.commissions` — même source de vérité
 * que la section admin « Commissions partenaires affiliés ».
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User2, Sparkles, Tag, Handshake, Percent, ArrowUpRight, ArrowDownRight,
  Minus, TrendingUp, Building2, Download, FileText, Calendar, CalendarDays,
  Fingerprint, Mail, Wallet, ClipboardList, PieChart, Ticket, ChevronRight,
  ArrowLeft, ArrowRight, Eye, Award, MapPin, Store, Briefcase,
  RefreshCcw, Undo2, AlertCircle,
} from "lucide-react";

import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import StickyPageHeader from "../components/StickyPageHeader";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import PartnerCodeOfferDetails from "../components/PartnerCodeOfferDetails";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";

// ─── Utilitaires ─────────────────────────────────────────────────────────
const fmtMoney = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const fmtMonth = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("fr-FR", {
    month: "long", year: "numeric",
  });
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const yearStartIso = () => `${new Date().getFullYear()}-01-01`;

const STATUS_LABEL = {
  active: { label: "Actif", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  upcoming: { label: "À venir", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  expired: { label: "Expiré", cls: "bg-zinc-200 text-zinc-700 border-zinc-300" },
  disabled: { label: "Désactivé", cls: "bg-red-100 text-red-800 border-red-200" },
};

const CATEGORY_LABEL = {
  agence: "Agence affiliée",
  pdv: "Point de vente affilié",
  apporteur: "Apporteur d'affaires",
  autre: "Autre partenariat",
};

// Couleurs par période (façon Dashboard collaborateur)
const PERIOD_META = {
  day:   { label: "Aujourd'hui",   icon: CalendarDays, tint: "#0055A4" },
  week:  { label: "Cette semaine", icon: Calendar,     tint: "#0EA5E9" },
  month: { label: "Ce mois",       icon: PieChart,     tint: "#059669" },
  year:  { label: "Cette année",   icon: TrendingUp,   tint: "#EA580C" },
};

const SITE_ICON = { agence: Briefcase, pdv: Store, bureau: Building2 };
const SITE_TYPE_LABEL = {
  agence: "Agence",
  pdv: "Point de vente",
  bureau: "Bureau",
  chantier: "Chantier",
  entrepot: "Entrepôt",
};

// ─── Helpers période (semaine / mois / année) — miroir Royalties ─────────
const startOfIsoWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay(); // 0=dim … 6=sam
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
};
const endOfIsoWeek = (d) => {
  const s = startOfIsoWeek(d);
  s.setDate(s.getDate() + 6);
  return s;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const endOfYear = (d) => new Date(d.getFullYear(), 11, 31);
const rangeFor = (mode, anchor = new Date()) => {
  if (mode === "week")  return { from: startOfIsoWeek(anchor), to: endOfIsoWeek(anchor) };
  if (mode === "month") return { from: startOfMonth(anchor),   to: endOfMonth(anchor) };
  if (mode === "year")  return { from: startOfYear(anchor),    to: endOfYear(anchor) };
  return null;
};
const shiftRange = (mode, current, delta) => {
  const anchor = current?.from ? new Date(current.from) : new Date();
  if (mode === "week") anchor.setDate(anchor.getDate() + delta * 7);
  else if (mode === "month") anchor.setMonth(anchor.getMonth() + delta);
  else if (mode === "year") anchor.setFullYear(anchor.getFullYear() + delta);
  return rangeFor(mode, anchor);
};
const isoDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const labelForRange = (mode, range) => {
  if (!range?.from || !range?.to) return "—";
  const f = range.from.toLocaleDateString("fr-FR");
  const t = range.to.toLocaleDateString("fr-FR");
  if (mode === "week")  return `Semaine du ${f} au ${t}`;
  if (mode === "month") return range.from.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  if (mode === "year")  return `Année ${range.from.getFullYear()}`;
  return `Du ${f} au ${t}`;
};

// ─── Sous-composants ─────────────────────────────────────────────────────
function ProgressionBadge({ pct, testId }) {
  if (pct === null || pct === undefined) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-zinc-500 font-mono uppercase tracking-wider"
        data-testid={testId}
      >
        <Minus size={11} /> N/A
      </span>
    );
  }
  const up = pct >= 0;
  const cls = up ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-red-700 bg-red-50 border-red-200";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border uppercase tracking-wider ${cls}`}
      data-testid={testId}
    >
      <Icon size={11} />
      {up ? "+" : ""}
      {pct}%
    </span>
  );
}

function KpiCard({ block, periodKey }) {
  const meta = PERIOD_META[periodKey];
  const Icon = meta.icon;
  return (
    <div
      className="bg-white border border-zinc-200 p-5 rounded-sm hover:border-zinc-300 transition-colors"
      data-testid={`affiliate-kpi-${periodKey}`}
    >
      <div className="flex items-start justify-between mb-4">
        <p className="label-uppercase">{meta.label}</p>
        <span
          className="h-9 w-9 inline-flex items-center justify-center rounded-sm"
          style={{ backgroundColor: `${meta.tint}14`, color: meta.tint }}
        >
          <Icon size={18} strokeWidth={1.75} />
        </span>
      </div>
      <p className="font-display text-3xl font-bold tracking-tight text-zinc-900 tabular-nums">
        {fmtMoney(block?.ventes_ttc)}
      </p>
      <p className="text-xs text-zinc-500 mt-2 font-mono">
        Ventes TTC · {block?.tx_count || 0} tx
        {block?.refunds_count ? (
          <span className="text-red-600 ml-1">· {block.refunds_count} remb.</span>
        ) : null}
      </p>
      <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Commissions HT</p>
          <p className="text-lg font-display font-semibold text-emerald-700 tabular-nums leading-tight">
            {fmtMoney(block?.commissions_ht)}
          </p>
        </div>
        <ProgressionBadge
          pct={block?.progression?.ventes_pct}
          testId={`affiliate-progression-${periodKey}`}
        />
      </div>
    </div>
  );
}

const CODE_KIND_META = {
  promo:     { label: "Codes promotionnels", icon: Tag,       tint: "#EF4135" },
  affiliate: { label: "Codes d'affiliation", icon: Percent,   tint: "#0055A4" },
  referral:  { label: "Codes de parrainage", icon: Handshake, tint: "#9333EA" },
};

function CodeRow({ code, kind }) {
  const st = STATUS_LABEL[code.status] || STATUS_LABEL.disabled;
  const meta = CODE_KIND_META[kind] || CODE_KIND_META.promo;
  // Détails remise/offres uniquement pour promo & parrainage.
  const showOffers = kind === "promo" || kind === "referral";
  return (
    <div
      className="border border-zinc-200 rounded-sm p-3 space-y-2 hover:border-zinc-300 hover:shadow-sm transition-all bg-white"
      data-testid={`affiliate-code-${kind}-${code.id}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <span
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm shrink-0"
            style={{ backgroundColor: `${meta.tint}14`, color: meta.tint }}
          >
            <meta.icon size={14} strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <p className="font-mono font-semibold text-zinc-900 truncate">{code.code}</p>
            {code.label ? (
              <p className="text-xs text-zinc-600 mt-0.5 truncate">{code.label}</p>
            ) : null}
            {code.description ? (
              <p className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2" title={code.description}>
                {code.description}
              </p>
            ) : null}
          </div>
        </div>
        <Badge className={`${st.cls} border font-mono text-[10px] uppercase tracking-wider`}>
          {st.label}
        </Badge>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">Début</p>
          <p className="font-mono">{fmtDate(code.start_date)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">Fin</p>
          <p className="font-mono">{code.is_indefinite ? "∞" : fmtDate(code.end_date)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">Utilisations</p>
          <p className="font-mono tabular-nums font-semibold text-zinc-900">
            {code.stats?.usage_count ?? 0}
          </p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">CA net</p>
          <p className="font-mono tabular-nums font-semibold text-emerald-700">
            {fmtMoney(code.stats?.ca_net)}
          </p>
        </div>
      </div>
      {showOffers ? (
        <PartnerCodeOfferDetails
          discountType={code.discount_type}
          discountValue={code.discount_value}
          offers={code.offers}
          accent={meta.tint}
          testIdPrefix={`affiliate-code-${kind}-${code.id}`}
        />
      ) : null}
      {code.stats?.services?.length ? (
        <div className="pt-2 border-t border-zinc-100">
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono mb-1 flex items-center gap-1">
            <Ticket size={9} /> Services / produits associés (top 10)
          </p>
          <div className="flex flex-wrap gap-1">
            {code.stats.services.map((s) => (
              <span
                key={s.id}
                className="text-[11px] font-mono bg-zinc-50 border border-zinc-200 rounded-sm px-1.5 py-0.5"
                title={`${s.count} tx · ${fmtMoney(s.net)}`}
              >
                {s.name} · {s.count}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SiteRow({ site }) {
  const Icon = SITE_ICON[site.type] || Building2;
  const brandColor = site.brand?.color || "#0055A4";
  return (
    <div
      className="flex items-center justify-between gap-3 py-3 flex-wrap"
      data-testid={`affiliate-site-${site.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className="h-10 w-10 inline-flex items-center justify-center rounded-sm shrink-0 border"
          style={{ backgroundColor: `${brandColor}14`, color: brandColor, borderColor: `${brandColor}33` }}
        >
          <Icon size={16} strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <p className="font-semibold text-zinc-900 truncate">{site.name || "—"}</p>
          <p className="text-xs text-zinc-500 font-mono truncate">
            {SITE_TYPE_LABEL[site.type] || site.type}
            {site.brand?.name ? ` · ${site.brand.name}` : ""}
            {site.city ? ` · ${site.city}` : ""}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {site.is_commissionable ? (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border font-mono text-[10px] uppercase tracking-wider">
            Commissionable
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            Non commissionable
          </Badge>
        )}
        {Number(site.commission_amount) > 0 ? (
          <span className="font-mono text-sm font-semibold text-emerald-700">
            {fmtMoney(site.commission_amount)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ProfileTile({ icon: Icon, label, value, tint = "#059669", testid, children }) {
  return (
    <div
      className="flex items-start gap-3 bg-white/70 backdrop-blur border border-white rounded-sm p-3"
      data-testid={testid}
    >
      <span
        className="h-9 w-9 inline-flex items-center justify-center rounded-sm shrink-0"
        style={{ backgroundColor: `${tint}18`, color: tint }}
      >
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">{label}</p>
        {value !== undefined ? (
          <p className="text-sm font-semibold text-zinc-900 mt-0.5 truncate">{value}</p>
        ) : null}
        {children}
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────
export default function EspaceAffiliation() {
  const { bid: bidParam } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Mode consultation admin/manager : l'URL contient un :bid ET l'utilisateur
  // n'est PAS un partenaire.
  const adminConsult = Boolean(bidParam) && user?.role !== "partenaire";

  const [profile, setProfile] = useState(null);
  const [codes, setCodes] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [breakdown, setBreakdown] = useState({ by_service: [], by_brand: [] });
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Historique tx — filtres période (miroir Relevés Royalties)
  const [periodMode, setPeriodMode] = useState("year");
  const [range, setRange] = useState(() => rangeFor("year"));
  const [customFrom, setCustomFrom] = useState(yearStartIso());
  const [customTo, setCustomTo] = useState(todayIso());
  const [txs, setTxs] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [detailTx, setDetailTx] = useState(null);

  const txStart = periodMode === "custom" ? customFrom : (range?.from ? isoDate(range.from) : yearStartIso());
  const txEnd = periodMode === "custom" ? customTo : (range?.to ? isoDate(range.to) : todayIso());

  const applyPeriodMode = (m) => {
    setPeriodMode(m);
    if (m !== "custom") setRange(rangeFor(m));
  };
  const navPeriod = (delta) => {
    if (periodMode === "custom") return;
    setRange((cur) => shiftRange(periodMode, cur, delta));
  };
  const periodLabel = useMemo(() => {
    if (periodMode === "custom") {
      return `Du ${new Date(customFrom).toLocaleDateString("fr-FR")} au ${new Date(customTo).toLocaleDateString("fr-FR")}`;
    }
    return labelForRange(periodMode, range);
  }, [periodMode, range, customFrom, customTo]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = adminConsult ? { bid: bidParam } : {};
        // Chargement résilient : `allSettled` afin qu'une seule API défaillante
        // (ex. sites vide, breakdown transitoire) ne masque PAS toutes les autres
        // sections. Le dashboard s'affiche avec ce qui est disponible et un toast
        // précise l'endpoint fautif s'il y en a un.
        const results = await Promise.allSettled([
          api.get("/affiliate/me", { params }),
          api.get("/affiliate/me/codes", { params }),
          api.get("/affiliate/me/kpis", { params }),
          api.get("/affiliate/me/breakdown", { params }),
          api.get("/affiliate/me/sites", { params }),
        ]);
        if (!alive) return;
        const [me, cds, kp, bd, st] = results;
        const failed = [];
        if (me.status === "fulfilled") setProfile(me.value.data); else failed.push("profil");
        if (cds.status === "fulfilled") setCodes(cds.value.data || []); else { setCodes([]); failed.push("codes"); }
        if (kp.status === "fulfilled") setKpis(kp.value.data); else failed.push("KPI");
        if (bd.status === "fulfilled") setBreakdown(bd.value.data || { by_service: [], by_brand: [] }); else { setBreakdown({ by_service: [], by_brand: [] }); failed.push("ventilations"); }
        if (st.status === "fulfilled") setSites(st.value.data || []); else { setSites([]); failed.push("sites"); }
        // Le "profil" est le seul élément vraiment bloquant : sans lui, on ne
        // sait même pas qui on consulte. Les autres sections peuvent être vides
        // sans casser l'expérience.
        if (me.status === "rejected") {
          const errMsg = me.reason?.response?.data?.detail || me.reason?.message || "";
          console.error("[Affiliate dashboard] Profil KO:", errMsg, me.reason);
          const readable = typeof errMsg === "string" && errMsg ? errMsg : "consultation impossible";
          toast.error(`Erreur de chargement du tableau de bord — ${readable}`);
        } else if (failed.length) {
          console.warn("[Affiliate dashboard] chargement partiel:", failed);
          toast.warning(`Chargement partiel — sections indisponibles : ${failed.join(", ")}`);
        }
      } catch (e) {
        console.error("[Affiliate dashboard] fatal:", e);
        toast.error("Erreur de chargement du tableau de bord");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [adminConsult, bidParam]);

  // Chargement de l'historique tx (à chaque changement de dates)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (loading) return;
      setTxLoading(true);
      try {
        const params = { start: txStart, end: txEnd };
        if (adminConsult) params.bid = bidParam;
        const r = await api.get("/affiliate/me/transactions", { params });
        if (alive) setTxs(r.data || []);
      } catch (e) {
        console.error("[Affiliate transactions] KO:", e);
        if (alive) {
          const detail = e?.response?.data?.detail || e?.message || "";
          const suffix = typeof detail === "string" && detail ? ` — ${detail}` : "";
          toast.error(`Erreur chargement des transactions${suffix}`);
        }
      } finally {
        if (alive) setTxLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [txStart, txEnd, loading, adminConsult, bidParam]);

  const totalActiveCodes = useMemo(() => {
    if (!codes) return 0;
    return ["promo", "affiliate", "referral"].reduce(
      (s, k) => s + (codes[k] || []).filter((c) => c.is_active).length, 0
    );
  }, [codes]);

  const brandsMap = useMemo(() => {
    const m = {};
    (profile?.brands || []).forEach((b) => { m[b.id] = b; });
    (breakdown.by_brand || []).forEach((b) => { if (b.id && !m[b.id]) m[b.id] = { id: b.id, name: b.name }; });
    return m;
  }, [profile, breakdown]);

  const downloadPdf = async () => {
    try {
      setDownloading(true);
      const params = { start: txStart, end: txEnd };
      if (adminConsult) params.bid = bidParam;
      const res = await api.get("/affiliate/me/transactions/pdf", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `historique_transactions_${profile?.partner_id}_${txStart}_${txEnd}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de télécharger l'historique PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="affiliate-loading">
        <Skeleton className="h-40 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-44" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="affiliate-dashboard">
      {/* ═══════════ Bandeau mode consultation admin/manager ═══════════ */}
      {adminConsult && (
        <div
          className="flex items-center justify-between gap-3 flex-wrap border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 rounded-sm shadow-sm"
          data-testid="affiliate-admin-consult-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-amber-600 text-white shrink-0">
              <Eye size={16} strokeWidth={1.8} />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-mono leading-none mb-1">
                Mode consultation à distance
              </p>
              <p className="text-sm font-semibold text-amber-950 truncate">
                Vous consultez l'espace personnel de <span className="font-mono">{profile?.name || "…"}</span>
                {profile?.partner_id ? (
                  <span className="ml-1 text-xs font-mono text-amber-700">· {profile.partner_id}</span>
                ) : null}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/kpi-transactions?tab=commissions&sub=beneficiaires")}
            data-testid="affiliate-admin-consult-back"
            className="border-amber-300 text-amber-800 hover:bg-amber-100 hover:text-amber-900 shrink-0"
          >
            <ArrowLeft size={13} className="mr-1.5" />
            Retour à la liste
          </Button>
        </div>
      )}

      {/* ═══════════ En-tête de page ═══════════ */}
      <StickyPageHeader testid="affiliate-sticky-header">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-uppercase mb-2">Espace personnel — Tableau de bord</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900 flex items-center gap-3">
              <Sparkles size={28} strokeWidth={1.5} className="text-emerald-600" />
              Bonjour {profile?.name || "Partenaire"}
            </h1>
            <p className="text-sm text-zinc-600 mt-1 font-mono">
              Suivi temps réel de votre activité · {totalActiveCodes} code(s) actif(s) · {sites.length} site(s) rattaché(s)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                Cumul annuel Commissions HT
              </p>
              <p className="font-display text-2xl font-bold text-emerald-700 tabular-nums" data-testid="affiliate-year-total">
                {fmtMoney(kpis?.year?.commissions_ht)}
              </p>
            </div>
          </div>
        </div>
      </StickyPageHeader>

      {/* ═══════════ Section 1 : Mes informations ═══════════ */}
      <Card
        className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50 overflow-hidden relative"
        data-testid="affiliate-profile"
      >
        <div
          className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-emerald-200 to-teal-200 opacity-40 blur-2xl"
          aria-hidden
        />
        <CardHeader className="pb-3 relative">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-emerald-600 text-white">
              <User2 size={16} strokeWidth={1.75} />
            </span>
            <div>
              <CardTitle className="text-base font-display">Mes informations</CardTitle>
              <p className="text-xs text-zinc-500 mt-0.5">
                Identité & conditions du partenariat d'affiliation
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 relative">
          <ProfileTile
            icon={User2}
            label="Partenaire"
            tint="#059669"
            testid="affiliate-tile-name"
          >
            <p className="text-sm font-semibold text-zinc-900 mt-0.5" data-testid="affiliate-name">
              {profile?.name || "—"}
            </p>
            <p className="text-xs text-zinc-500 font-mono mt-0.5 truncate flex items-center gap-1">
              <Mail size={10} /> {profile?.email}
            </p>
          </ProfileTile>
          <ProfileTile
            icon={Fingerprint}
            label="ID Partenaire"
            tint="#0EA5E9"
            testid="affiliate-tile-id"
          >
            <p
              className="text-sm font-mono font-bold text-sky-700 mt-0.5 tracking-wider"
              data-testid="affiliate-id"
            >
              {profile?.partner_id || "—"}
            </p>
          </ProfileTile>
          <ProfileTile
            icon={Award}
            label="Catégorie partenariat"
            tint="#7C3AED"
            testid="affiliate-tile-category"
          >
            <p
              className="text-sm font-semibold text-violet-700 mt-0.5"
              data-testid="affiliate-category"
            >
              {CATEGORY_LABEL[profile?.partnership_category] || "—"}
            </p>
          </ProfileTile>
          <ProfileTile
            icon={CalendarDays}
            label="Période contractuelle"
            tint="#EA580C"
            testid="affiliate-tile-contract"
          >
            <p className="font-mono text-xs text-zinc-900 mt-0.5">
              {fmtDate(profile?.contract_start)}
            </p>
            <p className="font-mono text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
              <ChevronRight size={10} />
              {profile?.contract_end ? fmtDate(profile?.contract_end) : "∞ (indéterminée)"}
            </p>
          </ProfileTile>
          <ProfileTile
            icon={Building2}
            label="Départements associés"
            tint="#0055A4"
            testid="affiliate-tile-brands"
          >
            <div className="flex flex-wrap gap-1 mt-1" data-testid="affiliate-brands">
              {(profile?.brands || []).length === 0 ? (
                <span className="text-xs text-zinc-500">—</span>
              ) : (
                profile.brands.map((b) => (
                  <span
                    key={b.id}
                    className="text-[10px] uppercase tracking-wider font-mono border rounded-sm px-1.5 py-0.5"
                    style={{
                      background: `${b.color || "#0055A4"}18`,
                      color: b.color || "#0055A4",
                      borderColor: `${b.color || "#0055A4"}40`,
                    }}
                  >
                    {b.name}
                  </span>
                ))
              )}
            </div>
          </ProfileTile>
          <ProfileTile
            icon={Wallet}
            label="Statut du partenariat"
            tint="#059669"
            testid="affiliate-tile-status"
          >
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border font-mono text-[10px] uppercase tracking-wider mt-0.5">
              Actif
            </Badge>
          </ProfileTile>
        </CardContent>
      </Card>

      {/* ═══════════ KPI CA net TTC + Commissions + Progression ═══════════ */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="h-8 w-8 inline-flex items-center justify-center rounded-sm bg-gradient-to-br from-sky-500 to-blue-600 text-white">
            <TrendingUp size={14} strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="text-sm font-display font-semibold text-zinc-900 uppercase tracking-wider">
              Ventes TTC · Commissions HT · Progression
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Cumuls temps réel — évolution comparée à la période précédente
            </p>
          </div>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
          data-testid="affiliate-kpis"
        >
          <KpiCard block={kpis?.day}   periodKey="day" />
          <KpiCard block={kpis?.week}  periodKey="week" />
          <KpiCard block={kpis?.month} periodKey="month" />
          <KpiCard block={kpis?.year}  periodKey="year" />
        </div>

        {/* Détail par département (année en cours) */}
        {breakdown.by_brand?.length ? (
          <Card className="mt-3 border-zinc-200" data-testid="affiliate-brand-breakdown">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 inline-flex items-center justify-center rounded-sm bg-blue-50 text-blue-700">
                  <Building2 size={13} strokeWidth={1.75} />
                </span>
                <CardTitle className="text-xs uppercase tracking-wider font-mono text-zinc-700">
                  Détail par département · Cette année
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="divide-y divide-zinc-100">
                {breakdown.by_brand.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center justify-between py-2.5 text-sm gap-3 flex-wrap"
                    data-testid={`affiliate-brand-detail-${b.id}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span
                        className="h-3 w-3 rounded-sm shrink-0"
                        style={{ background: brandsMap[b.id]?.color || "#059669" }}
                      />
                      <span className="font-medium truncate">{b.name || brandsMap[b.id]?.name || "—"}</span>
                    </span>
                    <span className="font-mono tabular-nums flex items-center gap-4">
                      <span className="text-zinc-700">
                        <span className="text-[10px] text-zinc-400 uppercase tracking-wider mr-1">Ventes TTC</span>
                        {fmtMoney(b.net)}
                      </span>
                      <span className="text-emerald-700 font-semibold">
                        <span className="text-[10px] text-emerald-400 uppercase tracking-wider mr-1">Tx</span>
                        {b.count}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Détail par service / produit (année en cours) */}
        {breakdown.by_service?.length ? (
          <Card className="mt-3 border-zinc-200" data-testid="affiliate-service-breakdown">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 inline-flex items-center justify-center rounded-sm bg-emerald-50 text-emerald-700">
                  <Ticket size={13} strokeWidth={1.75} />
                </span>
                <CardTitle className="text-xs uppercase tracking-wider font-mono text-zinc-700">
                  Détail par service / produit · Cette année
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="divide-y divide-zinc-100">
                {breakdown.by_service.slice(0, 15).map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between py-2.5 text-sm gap-3 flex-wrap"
                    data-testid={`affiliate-service-detail-${s.id}`}
                  >
                    <span className="font-medium truncate">{s.name || "—"}</span>
                    <span className="font-mono tabular-nums flex items-center gap-4">
                      <span className="text-zinc-700">
                        <span className="text-[10px] text-zinc-400 uppercase tracking-wider mr-1">Ventes TTC</span>
                        {fmtMoney(s.net)}
                      </span>
                      <span className="text-emerald-700 font-semibold">
                        <span className="text-[10px] text-emerald-400 uppercase tracking-wider mr-1">Tx</span>
                        {s.count}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      {/* ═══════════ Codes (affiliation / promo / parrainage) ═══════════ */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 inline-flex items-center justify-center rounded-sm bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
              <Sparkles size={14} strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="text-sm font-display font-semibold text-zinc-900 uppercase tracking-wider">
                Mes codes
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">
                Statut & performances de vos codes rattachés
              </p>
            </div>
          </div>
          <span className="text-xs text-zinc-500 font-mono">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 mr-1.5 animate-pulse" />
            {totalActiveCodes} code(s) actif(s)
          </span>
        </div>
        <Tabs defaultValue="affiliate" data-testid="affiliate-codes-tabs">
          <TabsList className="grid grid-cols-3 w-full sm:w-auto">
            <TabsTrigger value="affiliate" data-testid="affiliate-tab-affiliate">
              <Percent size={12} className="mr-1 text-blue-500" /> Affiliation ({codes?.affiliate?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="promo" data-testid="affiliate-tab-promo">
              <Tag size={12} className="mr-1 text-red-500" /> Promo ({codes?.promo?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="referral" data-testid="affiliate-tab-referral">
              <Handshake size={12} className="mr-1 text-purple-500" /> Parrainage ({codes?.referral?.length || 0})
            </TabsTrigger>
          </TabsList>
          {["affiliate", "promo", "referral"].map((k) => (
            <TabsContent key={k} value={k} className="mt-3">
              {(codes?.[k] || []).length === 0 ? (
                <p className="text-sm text-zinc-500 italic p-6 text-center border border-dashed border-zinc-200 rounded-sm bg-zinc-50/40">
                  Aucun code {k === "promo" ? "promotionnel" : k === "affiliate" ? "d'affiliation" : "de parrainage"} rattaché.
                </p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {codes[k].map((c) => <CodeRow key={c.id} code={c} kind={k} />)}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* ═══════════ Sites rattachés ═══════════ */}
      <Card className="border-zinc-200" data-testid="affiliate-sites">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-gradient-to-br from-sky-500 to-cyan-600 text-white">
              <MapPin size={16} strokeWidth={1.75} />
            </span>
            <div>
              <CardTitle className="text-base font-display">Mes sites associés</CardTitle>
              <p className="text-xs text-zinc-500 mt-0.5">
                Agences, points de vente et bureaux rattachés — commission au site
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {sites.length === 0 ? (
            <div className="p-8 text-center border border-dashed border-zinc-200 rounded-sm bg-zinc-50/40">
              <MapPin size={28} className="mx-auto text-zinc-300 mb-2" strokeWidth={1.5} />
              <p className="text-sm text-zinc-500 italic">
                Aucun site rattaché pour l'instant.
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                Les sites (Agence, PDV, Bureau) apparaîtront ici dès qu'un administrateur vous en aura rattaché.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {sites.map((s) => <SiteRow key={s.id} site={s} />)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══════════ Historique de transactions (filtrable + PDF + Détail) ═══════════ */}
      <Card className="border-zinc-200" data-testid="affiliate-transactions">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                <FileText size={16} strokeWidth={1.75} />
              </span>
              <div className="min-w-0">
                <CardTitle className="text-base font-display">Historique de transactions</CardTitle>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Ventes & remboursements limités à mes codes et sites d'affiliation
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadPdf}
              disabled={downloading || txs.length === 0}
              data-testid="affiliate-transactions-pdf"
              className="border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
            >
              <Download size={14} className="mr-1.5" />
              {downloading ? "…" : "Télécharger PDF"}
            </Button>
          </div>

          {/* Onglets période + navigation (miroir Relevés Royalties) */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Tabs value={periodMode} onValueChange={applyPeriodMode}>
              <TabsList className="rounded-sm" data-testid="affiliate-period-tabs">
                <TabsTrigger value="week" data-testid="affiliate-period-tab-week" className="text-xs">Semaine</TabsTrigger>
                <TabsTrigger value="month" data-testid="affiliate-period-tab-month" className="text-xs">Mois</TabsTrigger>
                <TabsTrigger value="year" data-testid="affiliate-period-tab-year" className="text-xs">Année</TabsTrigger>
                <TabsTrigger value="custom" data-testid="affiliate-period-tab-custom" className="text-xs">Personnalisé</TabsTrigger>
              </TabsList>
            </Tabs>

            {periodMode !== "custom" && (
              <div className="flex items-center gap-1 border border-zinc-200 rounded-sm bg-white h-8 px-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => navPeriod(-1)}
                  aria-label="Période précédente"
                  data-testid="affiliate-period-prev"
                >
                  <ArrowLeft size={14} />
                </Button>
                <span
                  className="text-xs font-mono capitalize px-2 min-w-[180px] text-center"
                  data-testid="affiliate-period-label"
                >
                  {periodLabel}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => navPeriod(1)}
                  aria-label="Période suivante"
                  data-testid="affiliate-period-next"
                >
                  <ArrowRight size={14} />
                </Button>
              </div>
            )}
          </div>

          {periodMode === "custom" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <Label htmlFor="tx-start" className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                  Du
                </Label>
                <Input
                  id="tx-start"
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  data-testid="affiliate-tx-start"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="tx-end" className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                  Au
                </Label>
                <Input
                  id="tx-end"
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  data-testid="affiliate-tx-end"
                  className="mt-1"
                />
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {txLoading ? (
            <div className="p-8 text-center text-sm text-zinc-500 font-mono">Chargement…</div>
          ) : txs.length === 0 ? (
            <div className="p-8 text-center border border-dashed border-zinc-200 rounded-sm bg-zinc-50/40">
              <ClipboardList size={28} className="mx-auto text-zinc-300 mb-2" strokeWidth={1.5} />
              <p className="text-sm text-zinc-500 italic">
                Aucune transaction sur la période sélectionnée.
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                Ajustez les dates pour élargir la recherche.
              </p>
            </div>
          ) : (
            <>
              {/* Résumé période — Ventes TTC + Remboursements + Commissions HT */}
              {(() => {
                const ventes = txs.filter((t) => t.type !== "remboursement" && Number(t.amount_ttc || 0) >= 0);
                const refunds = txs.filter((t) => t.type === "remboursement" || Number(t.amount_ttc || 0) < 0);
                const ventesTTC = ventes.reduce((s, t) => s + Number(t.amount_ttc || 0), 0);
                const refundsTTC = refunds.reduce((s, t) => s + Number(t.amount_ttc || 0), 0);
                const commissions = txs.reduce((s, t) => s + Number(t.partner_commission_ht || 0), 0);
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 pb-3 border-b border-zinc-100" data-testid="affiliate-tx-summary">
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Transactions</p>
                      <p className="font-display text-lg font-bold text-zinc-900 tabular-nums" data-testid="affiliate-tx-count">{txs.length}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Ventes TTC</p>
                      <p className="font-display text-lg font-bold text-blue-700 tabular-nums" data-testid="affiliate-tx-ventes-ttc">
                        {fmtMoney(ventesTTC)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Remboursements</p>
                      <p className="font-display text-lg font-bold text-red-700 tabular-nums" data-testid="affiliate-tx-refunds">
                        {fmtMoney(refundsTTC)}
                      </p>
                      <p className="text-[10px] text-zinc-500 font-mono">{refunds.length} tx</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Commissions HT</p>
                      <p className="font-display text-lg font-bold text-emerald-700 tabular-nums" data-testid="affiliate-tx-commissions">
                        {fmtMoney(commissions)}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Table desktop */}
              <div className="hidden md:block">
                <HorizontalScrollContainer
                  testId="affiliate-transactions-scroll"
                  className="border border-zinc-200 rounded-sm bg-white"
                >
                  <table className="w-full text-sm min-w-[1100px]">
                    <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                      <tr>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Type</th>
                        <th className="text-left px-3 py-2">Département</th>
                        <th className="text-left px-3 py-2">Site</th>
                        <th className="text-left px-3 py-2">Service</th>
                        <th className="text-left px-3 py-2">Client</th>
                        <th className="text-right px-3 py-2">TTC</th>
                        <th className="text-right px-3 py-2">Commission</th>
                        <th className="text-center px-3 py-2">Détail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {txs.map((t) => {
                        const isRefund = t.type === "remboursement" || (t.amount_ttc && Number(t.amount_ttc) < 0);
                        return (
                          <tr key={t.id} className="hover:bg-zinc-50/50" data-testid={`affiliate-tx-${t.id}`}>
                            <td className="px-3 py-2 font-mono text-xs">{fmtDate(t.date)}</td>
                            <td className="px-3 py-2">
                              <Badge
                                className={
                                  isRefund
                                    ? "bg-red-50 text-red-700 border-red-200 border text-[10px] uppercase"
                                    : "bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px] uppercase"
                                }
                              >
                                {isRefund ? (
                                  <>
                                    <Undo2 size={9} className="mr-0.5" /> remb.
                                  </>
                                ) : (
                                  t.type || "vente"
                                )}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {t.brand?.name ? (
                                <span
                                  className="text-[10px] uppercase tracking-wider font-mono border rounded-sm px-1.5 py-0.5"
                                  style={{
                                    background: `${t.brand.color || "#0055A4"}18`,
                                    color: t.brand.color || "#0055A4",
                                    borderColor: `${t.brand.color || "#0055A4"}40`,
                                  }}
                                >
                                  {t.brand.name}
                                </span>
                              ) : "—"}
                            </td>
                            <td className="px-3 py-2 text-xs truncate max-w-[140px]">{t.site?.name || "—"}</td>
                            <td className="px-3 py-2 text-xs truncate max-w-[160px]">{t.service_product?.name || "—"}</td>
                            <td className="px-3 py-2 text-xs truncate max-w-[140px]">{t.client_name || "—"}</td>
                            <td className={`px-3 py-2 text-right font-mono tabular-nums ${isRefund ? "text-red-700" : "text-zinc-900"}`}>
                              {fmtMoney(t.amount_ttc)}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-emerald-700">
                              {fmtMoney(t.partner_commission_ht)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => setDetailTx(t)}
                                aria-label="Voir le détail"
                                data-testid={`affiliate-tx-eye-${t.id}`}
                              >
                                <Eye size={14} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </HorizontalScrollContainer>
              </div>
              {/* Cartes mobile */}
              <div className="md:hidden space-y-2">
                {txs.map((t) => {
                  const isRefund = t.type === "remboursement" || (t.amount_ttc && Number(t.amount_ttc) < 0);
                  return (
                    <div key={t.id} className="border border-zinc-200 rounded-sm bg-white p-3" data-testid={`affiliate-tx-mobile-${t.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="font-mono text-xs text-zinc-500">{fmtDate(t.date)}</p>
                          <p className="font-medium truncate">{t.service_product?.name || "—"}</p>
                          <p className="text-xs text-zinc-500 truncate">{t.client_name || "—"}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Badge
                            className={
                              isRefund
                                ? "bg-red-50 text-red-700 border-red-200 border text-[10px] uppercase"
                                : "bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px] uppercase"
                            }
                          >
                            {isRefund ? "remb." : (t.type || "vente")}
                          </Badge>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => setDetailTx(t)}
                            aria-label="Voir le détail"
                            data-testid={`affiliate-tx-mobile-eye-${t.id}`}
                          >
                            <Eye size={14} />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-100 text-sm">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">TTC</p>
                          <p className={`font-mono tabular-nums font-semibold ${isRefund ? "text-red-700" : "text-zinc-900"}`}>
                            {fmtMoney(t.amount_ttc)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Commission</p>
                          <p className="font-mono tabular-nums font-semibold text-emerald-700">
                            {fmtMoney(t.partner_commission_ht)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ═══════════ Modal détail d'une transaction (icône œil) ═══════════ */}
      <Dialog open={!!detailTx} onOpenChange={(o) => !o && setDetailTx(null)}>
        <DialogContent className="max-w-2xl" data-testid="affiliate-tx-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={18} className="text-emerald-600" />
              Détail de la transaction
            </DialogTitle>
            <DialogDescription className="text-xs font-mono">
              {detailTx?.id ? `ID · ${detailTx.id}` : ""}
            </DialogDescription>
          </DialogHeader>
          {detailTx && (
            <div className="space-y-3">
              {(detailTx.type === "remboursement" || Number(detailTx.amount_ttc || 0) < 0) && (
                <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-sm">
                  <AlertCircle size={14} className="text-red-600 shrink-0" />
                  <p className="text-xs text-red-700 font-mono">
                    Cette transaction est un <b>remboursement / annulation</b>
                    {detailTx.refund_of ? ` (transaction source · ${detailTx.refund_of.slice(0, 8)}…)` : ""}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-zinc-50 p-3 rounded-sm border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">Date</p>
                  <p className="font-mono">{fmtDate(detailTx.date)}</p>
                </div>
                <div className="bg-zinc-50 p-3 rounded-sm border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">Type</p>
                  <p className="font-mono capitalize">{detailTx.type || "vente"}</p>
                </div>
                <div className="bg-zinc-50 p-3 rounded-sm border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">Département</p>
                  <p className="font-mono">{detailTx.brand?.name || "—"}</p>
                </div>
                <div className="bg-zinc-50 p-3 rounded-sm border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">Site</p>
                  <p className="font-mono">{detailTx.site?.name || "—"}{detailTx.site?.type ? ` · ${SITE_TYPE_LABEL[detailTx.site.type] || detailTx.site.type}` : ""}</p>
                </div>
                <div className="bg-zinc-50 p-3 rounded-sm border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">Service / produit</p>
                  <p className="font-mono">{detailTx.service_product?.name || "—"}</p>
                </div>
                <div className="bg-zinc-50 p-3 rounded-sm border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">Client</p>
                  <p className="font-mono truncate">{detailTx.client_name || "—"}</p>
                  {detailTx.client_email ? (
                    <p className="text-[10px] text-zinc-500 font-mono truncate mt-0.5">{detailTx.client_email}</p>
                  ) : null}
                </div>
                {detailTx.discount_code ? (
                  <div className="bg-emerald-50 p-3 rounded-sm border border-emerald-200 sm:col-span-2">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-mono mb-1">
                      Code {detailTx.discount_code.kind === "affiliate" ? "d'affiliation" : "promo"} utilisé
                    </p>
                    <p className="font-mono font-semibold text-emerald-900">
                      {detailTx.discount_code.code}
                      {detailTx.discount_code.label ? ` — ${detailTx.discount_code.label}` : ""}
                    </p>
                  </div>
                ) : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-3 border-t border-zinc-100">
                <div className="text-center bg-blue-50 p-2 rounded-sm border border-blue-100">
                  <p className="text-[10px] uppercase tracking-wider text-blue-700 font-mono">TTC</p>
                  <p className="font-display text-lg font-bold text-blue-900 tabular-nums">
                    {fmtMoney(detailTx.amount_ttc)}
                  </p>
                </div>
                <div className="text-center bg-sky-50 p-2 rounded-sm border border-sky-100">
                  <p className="text-[10px] uppercase tracking-wider text-sky-700 font-mono">CA net</p>
                  <p className="font-display text-lg font-bold text-sky-900 tabular-nums">
                    {fmtMoney(detailTx.ca_net)}
                  </p>
                </div>
                <div className="text-center bg-emerald-50 p-2 rounded-sm border border-emerald-100">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-mono">Commission HT</p>
                  <p className="font-display text-lg font-bold text-emerald-900 tabular-nums">
                    {fmtMoney(detailTx.partner_commission_ht)}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDetailTx(null)}
              data-testid="affiliate-tx-detail-close"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
